import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, queryString } from '../api';
import { EmptyState, ErrorNotice, Field, Notice, SelectField, Spinner } from '../components/ui';
import type { ApiarySearch } from '../types';

/**
 * Whose colonies are near a sighting.
 *
 * This screen exists for a bad morning: somebody has reported an Asian
 * Hornet and the question is who needs telephoning, now. So it opens ready
 * to be typed into, defaults to the 5km radius the response works to, and
 * puts the phone numbers in the results rather than behind a link to each
 * member's record.
 */
export function ApiariesPage() {
  const [postcode, setPostcode] = useState('');
  const [metres, setMetres] = useState('5000');
  const [result, setResult] = useState<ApiarySearch | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setResult(await api.get<ApiarySearch>(`/apiaries/near${queryString({ postcode, metres })}`));
    } catch (failure) {
      setResult(null);
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  const colonies = result?.within.reduce((sum, row) => sum + row.colony_count, 0) ?? 0;
  const members = new Set(result?.within.map((row) => row.member_id) ?? []).size;

  return (
    <>
      <div className="page-head">
        <h1>Apiaries near a sighting</h1>
      </div>

      <p className="muted">
        Enter the full postcode of the sighting. Everyone with colonies inside the radius is listed
        nearest first, with their phone numbers, so they can be rung.
      </p>

      <form className="card form wide" onSubmit={onSubmit}>
        <div className="grid">
          <Field
            label="Postcode of the sighting"
            required
            value={postcode}
            placeholder="PO30 1AE"
            hint="A full Isle of Wight postcode, not just the district."
            onChange={(e) => setPostcode(e.target.value)}
          />
          <SelectField label="Radius" value={metres} onChange={(e) => setMetres(e.target.value)}>
            <option value="1000">1 km</option>
            <option value="3000">3 km</option>
            <option value="5000">5 km — the usual response radius</option>
            <option value="10000">10 km</option>
            <option value="20000">20 km</option>
          </SelectField>
        </div>
        <div className="actions">
          <button type="submit" className="primary" disabled={busy}>
            {busy ? 'Searching…' : 'Find apiaries'}
          </button>
        </div>
      </form>

      <ErrorNotice error={error} />
      {busy ? <Spinner label="Searching" /> : null}

      {result !== null ? (
        <>
          <div className="page-head">
            <h2>
              {result.within.length === 0
                ? 'No apiaries in range'
                : `${result.within.length} ${result.within.length === 1 ? 'apiary' : 'apiaries'} within ${
                    result.radius_metres / 1000
                  } km`}
            </h2>
          </div>

          {result.within.length > 0 ? (
            <p className="muted small">
              {colonies} {colonies === 1 ? 'colony' : 'colonies'} belonging to {members}{' '}
              {members === 1 ? 'member' : 'members'}.
            </p>
          ) : null}

          {result.within.length === 0 ? (
            <EmptyState title="Nobody has an apiary recorded in that radius">
              That may genuinely be the case, or it may be that members have not recorded their
              apiaries yet. It is not proof there are no colonies there.
            </EmptyState>
          ) : (
            <table className="members">
              <thead>
                <tr>
                  <th scope="col">Distance</th>
                  <th scope="col">Member</th>
                  <th scope="col">Apiary</th>
                  <th scope="col">Colonies</th>
                  <th scope="col">Ring them on</th>
                </tr>
              </thead>
              <tbody>
                {result.within.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Distance">
                      <strong>{row.miles} mi</strong>
                      <span className="muted block small">{(row.metres / 1000).toFixed(1)} km</span>
                    </td>
                    <td data-label="Member">
                      <Link to={`/members/${row.member_id}`} className="member-name">
                        {row.first_name} {row.last_name}
                      </Link>
                      {row.status !== 'active' ? (
                        <span className="muted block small">{row.status}</span>
                      ) : null}
                    </td>
                    <td data-label="Apiary">
                      {row.label}
                      <span className="muted block small">{row.postcode}</span>
                    </td>
                    <td data-label="Colonies">{row.colony_count}</td>
                    <td data-label="Ring them on">
                      {row.telephone ? <a href={`tel:${row.telephone}`}>{row.telephone}</a> : null}
                      {row.mobile ? (
                        <a href={`tel:${row.mobile}`} className="block">
                          {row.mobile}
                        </a>
                      ) : null}
                      {!row.telephone && !row.mobile ? (
                        row.email ? (
                          <a href={`mailto:${row.email}`}>{row.email}</a>
                        ) : (
                          <span className="muted">no contact details</span>
                        )
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/*
            Never quietly omitted. An apiary we cannot place is neither inside
            nor outside the radius, and the honest thing in an incident is to
            say so rather than let somebody's colonies go unmentioned.
          */}
          {result.unplaced.length > 0 ? (
            <>
              <Notice kind="warning">
                {result.unplaced.length === 1
                  ? 'One apiary could not be placed'
                  : `${result.unplaced.length} apiaries could not be placed`}{' '}
                because the postcode is not a full Isle of Wight one. They are listed below whether
                or not they are near — check them by hand rather than assume they are out of range.
              </Notice>

              <table className="members">
                <thead>
                  <tr>
                    <th scope="col">Member</th>
                    <th scope="col">Apiary</th>
                    <th scope="col">Postcode given</th>
                    <th scope="col">Colonies</th>
                    <th scope="col">Ring them on</th>
                  </tr>
                </thead>
                <tbody>
                  {result.unplaced.map((row) => (
                    <tr key={row.id}>
                      <td data-label="Member">
                        <Link to={`/members/${row.member_id}`} className="member-name">
                          {row.first_name} {row.last_name}
                        </Link>
                      </td>
                      <td data-label="Apiary">{row.label}</td>
                      <td data-label="Postcode given">{row.postcode}</td>
                      <td data-label="Colonies">{row.colony_count}</td>
                      <td data-label="Ring them on">
                        {row.telephone ?? row.mobile ?? row.email ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </>
      ) : null}
    </>
  );
}
